import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

export default function BillingPage() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    setLoading(true);
    fetch(`/api/voice/billing?month=${month}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => setBilling(data))
      .catch(() => setBilling(null))
      .finally(() => setLoading(false));
  }, [month]);

  const s = billing?.summary || {};

  // Generate month options (last 12 months)
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    monthOptions.push({ val, label });
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/account" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
          <ChevronLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
          <p className="text-sm text-gray-500">Usage-based pricing · cost + 45% margin</p>
        </div>
      </div>

      {/* Month selector */}
      <div className="mb-6">
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        >
          {monthOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" />
        </div>
      ) : !billing ? (
        <p className="text-sm text-gray-400 text-center py-12">No billing data available</p>
      ) : (
        <div className="space-y-6">
          {/* Summary card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Summary</h2>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">${(s.totalCost || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-500">total for {billing.period?.month}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Voice', value: `${s.totalCalls || 0} calls`, sub: `${(s.inboundMinutes || 0) + (s.outboundMinutes || 0)} min` },
                { label: 'SMS', value: `${s.totalSms || 0} messages`, sub: `${s.smsOutbound || 0} out / ${s.smsInbound || 0} in` },
                { label: 'Meetings', value: `${s.meetingCount || 0} meetings`, sub: `${s.meetingMinutes || 0} min` },
                { label: 'Email', value: `${s.emailsSent || 0} sent`, sub: `${(s.emailFreeEmails || 3000).toLocaleString()} free` },
              ].map(item => (
                <div key={item.label} className="rounded-xl bg-gray-50 p-3">
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">{item.label}</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">{item.value}</p>
                  <p className="text-[11px] text-gray-400">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Cost breakdown */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Cost Breakdown</h2>
            <div className="space-y-1">
              {[
                { label: 'Voice Calls', detail: `${s.totalCalls || 0} calls · ${(s.inboundMinutes || 0) + (s.outboundMinutes || 0)} min`, cost: s.callCost, rate: `In: ${s.rates?.voiceInbound} · Out: ${s.rates?.voiceOutbound}` },
                { label: 'SMS Messages', detail: `${s.totalSms || 0} messages`, cost: s.smsCost, rate: s.rates?.smsOutbound + '/msg' },
                { label: 'Video Meetings', detail: `${s.meetingCount || 0} meetings · ${s.meetingMinutes || 0} min`, cost: s.meetingCost, rate: s.rates?.videoMeeting },
                { label: 'Email Delivery', detail: `${s.emailsSent || 0} emails sent`, cost: s.emailCost, rate: s.rates?.email },
                { label: 'Voicemail Transcription', detail: `${s.transcriptionCount || 0} transcriptions · ${s.transcriptionMinutes || 0} min`, cost: s.transcriptionCost, rate: s.rates?.transcription + '/min' },
                { label: 'Phone Numbers', detail: `${billing.numbers?.length || 0} active numbers`, cost: s.numberFees, rate: s.rates?.phoneNumber },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{item.label}</p>
                    <p className="text-xs text-gray-400">{item.detail}</p>
                    <p className="text-[10px] text-gray-300 mt-0.5">{item.rate}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 ml-4">${(item.cost || 0).toFixed(2)}</p>
                </div>
              ))}
              <div className="flex items-center justify-between pt-4 border-t-2 border-gray-200">
                <p className="text-base font-bold text-gray-900">Total</p>
                <p className="text-xl font-bold text-gray-900">${(s.totalCost || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Phone numbers */}
          {billing.numbers?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Phone Numbers</h2>
              <div className="space-y-2">
                {billing.numbers.map(n => (
                  <div key={n.phoneNumber} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800 font-mono">{n.phoneNumber}</p>
                      <p className="text-xs text-gray-400">{n.label}</p>
                    </div>
                    <p className="text-sm text-gray-600">${(n.monthlyCost || 0).toFixed(2)}/mo</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call details */}
          {billing.calls?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Call Details</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="pb-2 font-medium">Date</th>
                      <th className="pb-2 font-medium">Direction</th>
                      <th className="pb-2 font-medium">Number</th>
                      <th className="pb-2 font-medium text-right">Duration</th>
                      <th className="pb-2 font-medium text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billing.calls.map(call => (
                      <tr key={call.id} className="border-b border-gray-50">
                        <td className="py-2 text-gray-600">{new Date(call.date).toLocaleDateString()}</td>
                        <td className="py-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${call.direction === 'inbound' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                            {call.direction}
                          </span>
                        </td>
                        <td className="py-2 text-gray-700 font-mono text-xs">{call.direction === 'inbound' ? call.fromNumber : call.toNumber}</td>
                        <td className="py-2 text-right text-gray-600">{call.minutes} min</td>
                        <td className="py-2 text-right font-medium text-gray-900">${(call.cost || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SMS details */}
          {billing.sms?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">SMS Details</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="pb-2 font-medium">Date</th>
                      <th className="pb-2 font-medium">Direction</th>
                      <th className="pb-2 font-medium">Number</th>
                      <th className="pb-2 font-medium text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billing.sms.map(msg => (
                      <tr key={msg.id} className="border-b border-gray-50">
                        <td className="py-2 text-gray-600">{new Date(msg.date).toLocaleDateString()}</td>
                        <td className="py-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${msg.direction === 'inbound' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                            {msg.direction}
                          </span>
                        </td>
                        <td className="py-2 text-gray-700 font-mono text-xs">{msg.direction === 'inbound' ? msg.from : msg.to}</td>
                        <td className="py-2 text-right font-medium text-gray-900">${(msg.cost || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pricing reference */}
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Rate Card</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              {s.rates && Object.entries(s.rates).map(([key, rate]) => (
                <div key={key} className="flex justify-between gap-2">
                  <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <span className="text-gray-700 font-mono">{rate}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[10px] text-gray-400">All rates include 45% margin (incl. tax). ⅓ profit → jubly.org · ⅓ → dividends · ⅓ → investment</p>
          </div>
        </div>
      )}
    </div>
  );
}
